<template>
  <section>
    <spinner v-if="isLoading"></spinner>
    <errors v-if="isVisibleErrorsModal"></errors>
    <div class="content-wrapper">
      <div class="content-heading">Feedback</div>
      <div class="panel panel-default">
        <dismissable-alert
          v-if="hasProvidedFeedback"
          :title="previousFeedbackMessage"
          :closeAction="hidePreviousFeedbackMessage"
          :isShowing="showPreviousFeedbackMessage"
        />

        <dismissable-alert
          title="Thank you! Your feedback has been submitted."
          :closeAction="hideSubmittedAlert"
          :isShowing="feedbackWasSubmitted"
        />

        <div class="feedback-survey">
          <h3 class="survey-title">
            Thank you for taking a moment to provide feedback
          </h3>

          <div class="survey-question">
            <p class="question-text">
              How satisfied are you with your investment in the Community
              Investment Note?
            </p>

            <div class="scale-legend">
              <span>0 = Not at all</span>
              <span>10 = Very satisfied</span>
            </div>

            <div class="score-scale" role="radiogroup" aria-label="Satisfaction score from 0 to 10">
              <button
                v-for="value in scoreOptions"
                :key="`score-${value}`"
                type="button"
                class="score-option"
                :class="{ 'score-option-selected': score === value }"
                role="radio"
                :aria-checked="score === value ? 'true' : 'false'"
                :aria-label="`Score ${value}`"
                @click="selectScore(value)"
              >
                <span class="score-label">{{ value }}</span>
                <span class="score-circle"></span>
              </button>
            </div>
          </div>

          <div class="survey-question">
            <p class="question-text">
              What is the primary reason for your score? How could we improve
              your experience?
            </p>
            <textarea
              class="form-control reason-input"
              rows="4"
              maxlength="2000"
              placeholder="Enter text"
              v-model="reason"
            ></textarea>
          </div>
        </div>

        <button
          type="submit"
          class="btn btn-info btn-lg button-submit"
          :disabled="!canSubmit"
          @click="submit()"
        >
          Submit feedback
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';

import Errors from '@/components/modals/Errors';
import Spinner from '@/components/widgets/Spinner';
import DismissableAlert from '@/components/widgets/DismissableAlert';
import {
  SET_ERRORS_MODAL_INFO,
  SHOW_ERRORS_MODAL,
  SET_USER,
} from '@/store/mutation-types';

export default {
  name: 'feedbackSurvey',
  components: {
    errors: Errors,
    spinner: Spinner,
    'dismissable-alert': DismissableAlert,
  },
  data() {
    return {
      isLoading: false,
      score: null,
      reason: '',
      hasProvidedFeedback: false,
      lastFeedbackAt: null,
      showPreviousFeedbackMessage: true,
      feedbackWasSubmitted: false,
      scoreOptions: Array.from({ length: 11 }, (value, index) => index),
    };
  },
  async beforeMount() {
    await this.loadStatus();
  },
  computed: {
    ...mapState('errors', ['isVisibleErrorsModal']),
    ...mapState('app', ['user']),
    canSubmit() {
      return this.score !== null && !this.isLoading;
    },
    previousFeedbackMessage() {
      if (!this.lastFeedbackAt) return '';
      return `Thanks for your feedback on ${this.formatFeedbackDate(
        this.lastFeedbackAt,
      )}. Feel free to share more with us anytime.`;
    },
  },
  methods: {
    ...mapActions('feedback', ['getFeedbackStatus', 'submitFeedback']),
    ...mapMutations('errors', {
      showErrorsModal: SHOW_ERRORS_MODAL,
      setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
    }),
    ...mapMutations('app', {
      setUser: SET_USER,
    }),
    async loadStatus() {
      try {
        this.isLoading = true;
        const status = await this.getFeedbackStatus();
        this.hasProvidedFeedback = !!(status && status.hasProvidedFeedback);
        this.lastFeedbackAt = status ? status.lastFeedbackAt : null;
        this.showPreviousFeedbackMessage = this.hasProvidedFeedback;
      } catch (e) {
        // A missing status should not block the survey from being shown.
        this.hasProvidedFeedback = false;
      } finally {
        this.isLoading = false;
      }
    },
    selectScore(value) {
      this.score = value;
    },
    async submit() {
      if (!this.canSubmit) return;
      try {
        this.isLoading = true;
        const trimmedReason = this.reason.trim();
        const status = await this.submitFeedback({
          score: this.score,
          ...(trimmedReason ? { reason: trimmedReason } : {}),
        });
        this.hasProvidedFeedback = true;
        this.lastFeedbackAt =
          status && status.lastFeedbackAt
            ? status.lastFeedbackAt
            : new Date().toISOString();
        // Keep the cached user in sync so the sidebar Feedback indicator
        // clears immediately, without requiring a full page reload.
        if (this.user) {
          this.setUser({ ...this.user, lastFeedbackAt: this.lastFeedbackAt });
        }
        this.feedbackWasSubmitted = true;
        this.showPreviousFeedbackMessage = false;
        this.score = null;
        this.reason = '';
      } catch (e) {
        this.setErrorsModalInfo(['Something went wrong. Please try again.']);
        this.showErrorsModal();
      } finally {
        this.isLoading = false;
      }
    },
    hidePreviousFeedbackMessage() {
      this.showPreviousFeedbackMessage = false;
    },
    hideSubmittedAlert() {
      this.feedbackWasSubmitted = false;
    },
    formatFeedbackDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const month = date.toLocaleDateString('en-US', { month: 'long' });
      const day = date.getDate();
      return `${month} ${day}${this.ordinalSuffix(day)} ${date.getFullYear()}`;
    },
    ordinalSuffix(day) {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    },
  },
};
</script>

<style scoped>
.feedback-survey {
  padding: 10px 5px 5px;
}

.survey-title {
  font-size: 18px;
  font-weight: 600;
  color: #515253;
  margin-bottom: 25px;
}

.survey-question {
  margin-bottom: 30px;
}

.question-text {
  font-size: 16px;
  color: #656565;
  margin-bottom: 12px;
}

.scale-legend {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #909090;
  margin-bottom: 8px;
}

.score-scale {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.score-option {
  flex: 1 1 0;
  min-width: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.15s ease-in-out;
}

.score-option:hover {
  background-color: #f4f9fa;
}

.score-label {
  font-size: 14px;
  color: #656565;
}

.score-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #d3d3d3;
  background-color: #fff;
  transition: all 0.15s ease-in-out;
}

.score-option:hover .score-circle {
  border-color: #127285;
}

.score-option-selected .score-circle {
  border-color: #127285;
  background-color: #127285;
  box-shadow: inset 0 0 0 3px #fff;
}

.score-option-selected .score-label {
  color: #127285;
  font-weight: 600;
}

.reason-input {
  resize: vertical;
}

.button-submit {
  margin-top: 10px;
  background-color: #127285;
}

@media (max-width: 600px) {
  .score-option {
    min-width: 36px;
  }

  .score-label {
    font-size: 12px;
  }
}
</style>



// WEBPACK FOOTER //
// FeedbackSurvey.vue?07338ab7